import { createFormPdf, PDF_COLORS } from '../utils/formPdf';
import { getAuthHeaders } from '../utils/authHeaders';
import pvsLogo from '../assets/pvs-logo.png';

const API_URL = 'https://app.pvslimited.co.uk/api';

export const CALOR_REFERENCE = 'LCAL010023';
export const DELIVERY_ADDRESS = ['EMR Bradford', 'Planetrees Rd', 'Bradford BD4 8AE'];
export const COLLECTION_ADDRESS = ['Pressure Vessel Services', 'Coley Road', 'Halifax', 'HX3 7SA'];

const MAX_REFERENCE_ATTEMPTS = 3;

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(`${dateStr}T00:00:00`);
  if (Number.isNaN(d.getTime())) return dateStr;
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  return `${dd}/${mm}/${d.getFullYear()}`;
};

// A missing logo should never stop a driver submitting, so the note falls
// back to the letterhead text alone.
const loadLogoBytes = async () => {
  try {
    const response = await fetch(pvsLogo);
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return await response.arrayBuffer();
  } catch (error) {
    console.warn('PVS logo unavailable, building delivery note without it:', error);
    return null;
  }
};

export async function fetchNextReference() {
  const response = await fetch(`${API_URL}/cg-scrap-delivery-note/next-reference`, {
    headers: getAuthHeaders(),
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok || !body?.reference) {
    throw new Error(body?.error || `Failed to load PVS reference number (${response.status})`);
  }
  return body.reference;
}

const buildDeliveryNotePdf = async (formData, reference, logoBytes) => {
  const ticketBytes = await formData.weighbridgeTicket.arrayBuffer();
  return createFormPdf(
    {
      title: 'CG SCRAP DELIVERY NOTE',
      letterhead: {
        logo: logoBytes,
        lines: COLLECTION_ADDRESS,
        footer: COLLECTION_ADDRESS.join(', '),
      },
      references: [
        { label: 'PVS Reference No', value: reference },
        { label: 'Calor Reference No', value: CALOR_REFERENCE },
      ],
    },
    async (pdf) => {
      pdf.addressBlocks([
        { title: 'Collection Address', lines: COLLECTION_ADDRESS },
        { title: 'Delivery Address', lines: DELIVERY_ADDRESS },
      ]);

      pdf.section('Delivery Details');
      pdf.field('Driver Name', formData.driverName);
      pdf.field('Delivery Date', formatDate(formData.deliveryDate));
      pdf.field('Time Arrived On Site', formData.timeArrived);
      pdf.field('Time Leaving Site', formData.timeLeft);

      pdf.section('Load Description');
      formData.loadDescription
        .split(/\r?\n/)
        .filter((line) => line.trim())
        .forEach((line) => pdf.paragraph(line, { size: 10, color: PDF_COLORS.dark }));

      pdf.pageBreak();
      await pdf.image('Weighbridge Ticket', ticketBytes, formData.weighbridgeTicket.type, { maxHeight: 600 });
    },
  );
};

// The PDF is rebuilt and resubmitted if another driver claimed the reference
// first, so the number printed on the note always matches the one issued.
export async function submitCgScrapDeliveryNote(formData, initialReference, { onReferenceChange } = {}) {
  let reference = initialReference;
  const logoBytes = await loadLogoBytes();

  for (let attempt = 0; attempt < MAX_REFERENCE_ATTEMPTS; attempt += 1) {
    const pdfBlob = await buildDeliveryNotePdf(formData, reference, logoBytes);

    const body = new FormData();
    body.append('file', pdfBlob, `CG_Scrap_Delivery_Note_${reference}.pdf`);
    body.append('weighbridgeTicket', formData.weighbridgeTicket, formData.weighbridgeTicket.name);
    body.append('reference', reference);
    body.append('driverName', formData.driverName);
    body.append('deliveryDate', formData.deliveryDate);
    body.append('timeArrived', formData.timeArrived);
    body.append('timeLeft', formData.timeLeft);
    body.append('loadDescription', formData.loadDescription);

    const response = await fetch(`${API_URL}/cg-scrap-delivery-note/send-email`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body,
    });
    const payload = await response.json().catch(() => ({}));

    if (response.ok) return payload.reference || reference;

    if (response.status === 409 && payload.code === 'REFERENCE_TAKEN' && payload.nextReference) {
      reference = payload.nextReference;
      onReferenceChange?.(reference);
      continue;
    }

    throw new Error(payload.error || 'Failed to send email');
  }

  throw new Error('Unable to allocate a PVS reference number. Please try again.');
}

export default submitCgScrapDeliveryNote;
